import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import styles from './styles.module.scss';
import {
  PitchPlayer,
  PlayerRow,
  MobileLineupPitch,
} from './components';
import { useViewportMaxWidth } from './hooks/useViewportMaxWidth';
import { mapMatchDetailsToAlineaciones } from './utils/mapMatchDetailsLineups';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import PlaceholderJugador from 'containers/Mundial2026/assets/placeholders/Jugador.png';

const TRANSLATION_KEYS = {
  directorTecnicoLabel: 'eventodeportivo_partidos_alineaciones_texto_titulo_directortecnico',
  sustitutosTitle: 'eventodeportivo_partidos_alineaciones_texto_titulo_sustitutos',
};

const DIRECTOR_TECNICO_LABEL_MAX_LENGTH = 20;
const SUSTITUTOS_TITLE_MAX_LENGTH = 40;
const COACH_PILL_MAX_LENGTH = 20;

const FORMATION_DISPLAY_MAX_LENGTH = 7;

function formatPlayerNumberForTable(num, useMobileLayout) {
  if (!useMobileLayout) return num;
  if (num === null || num === undefined) return num;
  const str = String(num).trim();
  if (!/^\d+$/.test(str)) return num;
  return String(Number(str));
}

function CoachAvatar({ photoUrl }) {
  return (
    <span
      className={classNames('wca-lineaciones__avatar-ph', styles['wca-lineaciones__coach-avatar'])}
    >
      <img
        className="wca-lineaciones__avatar-ph-img"
        src={photoUrl || PlaceholderJugador}
        alt=""
        onError={e => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = PlaceholderJugador;
        }}
      />
    </span>
  );
}

function LineupsTeamIcon({ url }) {
  if (!url) {
    return <span className={styles['wca-lineaciones__lineup-head-flag--empty']} aria-hidden />;
  }
  return (
    <img
      className={styles['wca-lineaciones__lineup-head-flag']}
      src={url}
      alt=""
      width={20}
      height={14}
    />
  );
}

export default function WCAlineaciones({ matchDetailsData }) {
  const translations = useSelector((state) => state.translations);
  const asset = useSelector((state) => state.asset);

  const texts = useMemo(() => {
    const directorFull = getTranslationEmptyOrNullKey(
      TRANSLATION_KEYS.directorTecnicoLabel,
      translations,
    );
    const sustitutosFull = getTranslationEmptyOrNullKey(
      TRANSLATION_KEYS.sustitutosTitle,
      translations,
    );
    return {
      directorTecnicoLabel: renderMaxLengthText(directorFull, DIRECTOR_TECNICO_LABEL_MAX_LENGTH),
      directorTecnicoLabelFull: directorFull,
      sustitutosTitle: renderMaxLengthText(sustitutosFull, SUSTITUTOS_TITLE_MAX_LENGTH),
      sustitutosTitleFull: sustitutosFull,
    };
  }, [translations]);

  const [mobileTeam, setMobileTeam] = useState('home');
  const isMobile = useViewportMaxWidth(450);
  const isTablet = useViewportMaxWidth(768) && !isMobile;
  const useMobileLayout = isMobile || isTablet;
  const tabletRootRef = useRef(null);
  const pitchWrapRef = useRef(null);

  const desktopPitchBgUrl =
    typeof asset?.eventodeportivo_imagen_cancha === 'string' &&
    asset.eventodeportivo_imagen_cancha.trim()
      ? asset.eventodeportivo_imagen_cancha
      : null;
  const mobilePitchBgUrl =
    typeof asset?.eventodeportivo_imagen_mediacancha === 'string' &&
    asset.eventodeportivo_imagen_mediacancha.trim()
      ? asset.eventodeportivo_imagen_mediacancha
      : null;

  useLayoutEffect(() => {
    if (!isTablet || !tabletRootRef.current || !pitchWrapRef.current) return undefined;
    const el = tabletRootRef.current;
    const wrap = pitchWrapRef.current;
    const syncPitchSx = () => {
      const w = wrap.getBoundingClientRect().width;
      const sx = w / 372;
      el.style.setProperty('--wca-lineup-pitch-sx', String(sx));
      /* Contrapeso del scaleX de la cancha: las cards quedan con el mismo tamaño visual que en móvil. */
      el.style.setProperty('--wca-lineup-pitch-inv-sx', String(372 / w));
    };
    syncPitchSx();
    window.addEventListener('resize', syncPitchSx);
    return () => window.removeEventListener('resize', syncPitchSx);
  }, [isTablet, mobileTeam]);

  const mapped = useMemo(() => mapMatchDetailsToAlineaciones(matchDetailsData), [matchDetailsData]);

  const shouldShowBench = (mapped?.benchRows?.length ?? 0) > 0;

  const homeFormation = mapped?.homeFormation ?? '';
  const awayFormation = mapped?.awayFormation ?? '';

  const teamColorsStyle = useMemo(
    () => ({
      ...(mapped?.homeTeam?.primaryHex ? { '--wca-lineaciones-home-hex': mapped.homeTeam.primaryHex } : {}),
      ...(mapped?.awayTeam?.primaryHex ? { '--wca-lineaciones-away-hex': mapped.awayTeam.primaryHex } : {}),
    }),
    [mapped?.homeTeam?.primaryHex, mapped?.awayTeam?.primaryHex],
  );

  const homePitchPlayers = useMemo(
    () => mapped?.homePitchPlayers ?? [],
    [mapped?.homePitchPlayers, mapped?.homeFormation],
  );

  const awayPitchPlayers = useMemo(
    () => mapped?.awayPitchPlayers ?? [],
    [mapped?.awayPitchPlayers, mapped?.awayFormation],
  );

  const formationLabel = mobileTeam === 'home' ? homeFormation : awayFormation;

  const homeNameShort = mapped?.homeTeam?.code ?? mapped?.homeTeam?.teamId ?? '';
  const awayNameShort = mapped?.awayTeam?.code ?? mapped?.awayTeam?.teamId ?? '';
  const homeNameFull = mapped?.homeTeam?.name ?? '';
  const awayNameFull = mapped?.awayTeam?.name ?? '';

  if (!mapped) {
    return null;
  }

  const layout = (
    <>
      <div
        className={classNames(
          styles['wca-lineaciones__lineup-head'],
          styles['wca-lineaciones__lineup-head--desktop'],
        )}
      >
        <div className={styles['wca-lineaciones__lineup-head-side']}>
          <div className={styles['wca-lineaciones__lineup-head-top']}>
            <LineupsTeamIcon url={mapped.homeTeam?.flagUrl} />
            <span className={styles['wca-lineaciones__lineup-head-teamname']}>
              {useMobileLayout ? homeNameShort : homeNameFull}
            </span>
          </div>
          <span className={styles['wca-lineaciones__formation']} title={homeFormation}>
            {renderMaxLengthText(`${homeFormation}`, FORMATION_DISPLAY_MAX_LENGTH)}
          </span>
        </div>
        <div
          className={classNames(
            styles['wca-lineaciones__lineup-head-side'],
            styles['wca-lineaciones__lineup-head-side--away'],
          )}
        >
          <div className={styles['wca-lineaciones__lineup-head-top']}>
            <span className={styles['wca-lineaciones__lineup-head-teamname']}>
              {useMobileLayout ? awayNameShort : awayNameFull}
            </span>
            <LineupsTeamIcon url={mapped.awayTeam?.flagUrl} />
          </div>
          <span className={styles['wca-lineaciones__formation']} title={awayFormation}>
            {renderMaxLengthText(`${awayFormation}`, FORMATION_DISPLAY_MAX_LENGTH)}
          </span>
        </div>
      </div>

      <div
        className={classNames(
          styles['wca-lineaciones__team-switch'],
          styles['wca-lineaciones__team-switch--mobile'],
        )}
      >
        <button
          type="button"
          className={classNames(styles['wca-lineaciones__team-switch-btn'], {
            [styles['wca-lineaciones__team-switch-btn--active']]: mobileTeam === 'home',
          })}
          onClick={() => setMobileTeam('home')}
        >
          <LineupsTeamIcon url={mapped.homeTeam?.flagUrl} />
          <span>{homeNameShort}</span>
        </button>
        <button
          type="button"
          className={classNames(styles['wca-lineaciones__team-switch-btn'], {
            [styles['wca-lineaciones__team-switch-btn--active']]: mobileTeam === 'away',
          })}
          onClick={() => setMobileTeam('away')}
        >
          <LineupsTeamIcon url={mapped.awayTeam?.flagUrl} />
          <span>{awayNameShort}</span>
        </button>
      </div>

      <div className={styles['wca-lineaciones__formation-mobile']}>{formationLabel}</div>

      {!useMobileLayout ? (
        <div className={styles['wca-lineaciones__pitch-outer']}>
          <div
            className={styles['wca-lineaciones__pitch']}
            style={desktopPitchBgUrl ? { backgroundImage: `url(${desktopPitchBgUrl})` } : undefined}
          >
            <div className={styles['wca-lineaciones__pitch-surface']} aria-hidden="true">
              <div className={styles['wca-lineaciones__pitch-half-line']} />
              <div className={styles['wca-lineaciones__pitch-center-circle']} />
              <div className={styles['wca-lineaciones__pitch-center-spot']} />
              <div
                className={classNames(
                  styles['wca-lineaciones__pitch-penalty'],
                  styles['wca-lineaciones__pitch-penalty--left'],
                )}
              />
              <div
                className={classNames(
                  styles['wca-lineaciones__pitch-goal-area'],
                  styles['wca-lineaciones__pitch-goal-area--left'],
                )}
              />
              <div
                className={classNames(
                  styles['wca-lineaciones__pitch-penalty'],
                  styles['wca-lineaciones__pitch-penalty--right'],
                )}
              />
              <div
                className={classNames(
                  styles['wca-lineaciones__pitch-goal-area'],
                  styles['wca-lineaciones__pitch-goal-area--right'],
                )}
              />
            </div>
            {homePitchPlayers.map((p, i) => (
              <PitchPlayer key={`ar-${i}`} {...p} />
            ))}
            {awayPitchPlayers.map((p, i) => (
              <PitchPlayer key={`at-${i}`} {...p} />
            ))}
          </div>
        </div>
      ) : mobileTeam === 'home' ? (
        <MobileLineupPitch
          ref={pitchWrapRef}
          players={homePitchPlayers}
          isHome
          formationStr={homeFormation}
          formationFallback={mapped.homeFormationLines}
          backgroundImageUrl={mobilePitchBgUrl}
        />
      ) : (
        <MobileLineupPitch
          ref={pitchWrapRef}
          players={awayPitchPlayers}
          isHome={false}
          formationStr={awayFormation}
          formationFallback={mapped.awayFormationLines}
          backgroundImageUrl={mobilePitchBgUrl}
        />
      )}

      <div className={styles['wca-lineaciones__coaches']}>
        <div className={styles['wca-lineaciones__coaches-labels']}>
          <span className={styles['wca-lineaciones__coaches-label']} title={texts.directorTecnicoLabelFull}>
            {texts.directorTecnicoLabel}
          </span>
          <span className={styles['wca-lineaciones__coaches-label']} title={texts.directorTecnicoLabelFull}>
            {texts.directorTecnicoLabel}
          </span>
        </div>
        <div className={styles['wca-lineaciones__coaches-row']}>
          <div className={styles['wca-lineaciones__coach']}>
            <CoachAvatar photoUrl={mapped.homeCoachPhoto} />
            <span
              className={styles['wca-lineaciones__coach-pill']}
              title={mapped.homeCoach}
            >
              {renderMaxLengthText(`${mapped.homeCoach}`, COACH_PILL_MAX_LENGTH)}
            </span>
          </div>
          <div
            className={classNames(styles['wca-lineaciones__coach'], styles['wca-lineaciones__coach--away'])}
          >
            <span
              className={styles['wca-lineaciones__coach-pill']}
              title={mapped.awayCoach}
            >
              {renderMaxLengthText(`${mapped.awayCoach}`, COACH_PILL_MAX_LENGTH)}
            </span>
            <CoachAvatar photoUrl={mapped.awayCoachPhoto} />
          </div>
        </div>
      </div>

      <div className={styles['wca-lineaciones__list']}>
        {mapped.starterRows.map((row) => (
          <PlayerRow
            key={row.key}
            numHome={formatPlayerNumberForTable(row.numHome, useMobileLayout)}
            numAway={formatPlayerNumberForTable(row.numAway, useMobileLayout)}
            nameHome={row.nameHome}
            nameAway={row.nameAway}
            eventsHome={row.eventsHome}
            eventsAway={row.eventsAway}
            subHome={row.subHome}
            subAway={row.subAway}
            useMobileLayout={useMobileLayout}
          />
        ))}
      </div>

      {shouldShowBench ? (
        <>
          <div className={styles['wca-lineaciones__bench-title']} title={texts.sustitutosTitleFull}>
            {texts.sustitutosTitle}
          </div>
          <div
            className={classNames(styles['wca-lineaciones__list'], styles['wca-lineaciones__list--bench'])}
          >
            {mapped.benchRows.map((row) => (
              <PlayerRow
                key={row.key}
                numHome={formatPlayerNumberForTable(row.numHome, useMobileLayout)}
                numAway={formatPlayerNumberForTable(row.numAway, useMobileLayout)}
                nameHome={row.nameHome}
                nameAway={row.nameAway}
                eventsHome={row.eventsHome}
                eventsAway={row.eventsAway}
                subHome={row.subHome}
                subAway={row.subAway}
                useMobileLayout={useMobileLayout}
              />
            ))}
          </div>
        </>
      ) : null}
    </>
  );

  return (
    <div
      ref={isTablet ? tabletRootRef : undefined}
      className={isTablet ? styles['wca-lineaciones--tablet'] : undefined}
      style={teamColorsStyle}
    >
      {layout}
    </div>
  );
}
